import { SupabaseClient } from '@supabase/supabase-js';
import React, { useState, useRef, useEffect } from 'react';
import { RedirectTo } from '@habity/types/auth';
import { Button, Input, Stack, Text } from '@chakra-ui/react';
import { useToast } from '~/lib/toasts';
import { useTranslations } from 'next-intl';
import { TODO } from '@habity/types';
import { getAPI, postAPI } from '~/lib/api';
import { useTracking } from '~/hooks/useTracking';
import { API_PATHS, EVENTS } from '@habity/constants';
import {
  SignupForm,
  SignupFormValues,
  UseFormReturn,
} from '~/components/ui/forms/SignupForm';
import { parsePhoneNumber } from 'awesome-phonenumber';

interface UserLookupResult {
  exists: boolean;
  hasCompleteProfile: boolean;
}

function MagicLink({
  // setAuthView = () => null,
  redirectTo,
  agencyId,
  onStepChange,
}: // showLinks = false,
{
  setAuthView?: TODO;
  supabaseClient?: SupabaseClient; // Made optional since it's not used
  redirectTo?: RedirectTo;
  showLinks?: boolean;
  agencyId: number;
  onStepChange?: (step: 1 | 2) => void;
}) {
  const [email, setEmail] = useState('');
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [userLookupResult, setUserLookupResult] =
    useState<UserLookupResult | null>(null);
  const { showToast } = useToast();
  const t = useTranslations('Auth.magic_link');
  const { track } = useTracking();
  const formRef = useRef<UseFormReturn<SignupFormValues>>(null);

  // Notify parent when step changes
  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  // Check if user exists
  const checkUserProfile = async (email: string): Promise<UserLookupResult> => {
    try {
      const response = await postAPI(
        API_PATHS.PROVIDERS.SUPABASE.USERS.LOOKUP,
        JSON.stringify({ email, agency_id: agencyId })
      );

      if (response.error) {
        return { exists: false, hasCompleteProfile: false };
      }

      return {
        exists: response.exists || false,
        hasCompleteProfile: response.hasCompleteProfile || false,
      };
    } catch (error) {
      console.error('Error checking user profile:', error);
      return { exists: false, hasCompleteProfile: false };
    }
  };

  const handleMagicLinkSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const lookupResult = await checkUserProfile(email);
      setUserLookupResult(lookupResult);

      if (lookupResult.exists && lookupResult.hasCompleteProfile) {
        // User exists with complete profile - send magic link
        await sendMagicLink(email);
      } else {
        // User doesn't exist or profile incomplete - go to step 2
        setCurrentStep(2);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error in login:', error);
      showToast(
        {
          title: 'Något gick fel',
          description: 'Försök igen senare',
        },
        { status: 'error' }
      );
      setLoading(false);
    }
  };

  const sendMagicLink = async (emailAddress: string) => {
    await getAPI(
      `/providers/customerio/user/magiclink/${emailAddress}?redirectTo=${
        redirectTo || '/'
      }`
    );

    // Track magic link request event
    const trackingData: Record<string, unknown> = {
      email: emailAddress,
      redirectTo,
      agencyId,
    };

    // Only access window properties in browser environment
    if (typeof window !== 'undefined') {
      if (window.habityLoginHomeId)
        trackingData.homeId = window.habityLoginHomeId;
      if (window.habityLoginHomeStreet)
        trackingData.homeStreet = window.habityLoginHomeStreet;
      if (window.habityLoginHomeCity)
        trackingData.homeCity = window.habityLoginHomeCity;
      if (window.habityLoginActionType)
        trackingData.actionType = window.habityLoginActionType;
    }

    track(EVENTS.REQUESTED_MAGIC_LINK, trackingData);

    showToast({
      title: t('confirmation_title'),
      description: t('confirmation_text'),
    });
    setLoading(false);
  };

  const handleSignupSubmit = async (data: SignupFormValues) => {
    setLoading(true);
    try {
      let firstName = data.name;
      let lastName = '';

      const nameParts = data.name.trim().split(' ');
      if (nameParts.length > 1) {
        firstName = nameParts.slice(0, -1).join(' ');
        lastName = nameParts[nameParts.length - 1];
      }

      const parsedPhone = data.phone
        ? parsePhoneNumber(data.phone, { regionCode: 'SE' })
        : null;
      const formattedPhone = parsedPhone?.valid
        ? parsedPhone.number.e164
        : data.phone;

      const termsAcceptedAt = data.acceptTerms
        ? new Date().toISOString()
        : null;

      const privacyPolicyAcceptedAt = data.acceptPrivacyPolicy
        ? new Date().toISOString()
        : null;

      const userData = {
        email: data.email,
        name: data.name,
        first_name: firstName,
        last_name: lastName,
        phone: formattedPhone,
        terms_accepted_at: termsAcceptedAt,
        privacy_policy_accepted_at: privacyPolicyAcceptedAt,
        agency_id: agencyId,
      };

      // Create or update user
      await postAPI(
        '/providers/supabase/users',
        JSON.stringify({ email: data.email, userData })
      );

      // Send magic link
      await sendMagicLink(data.email);
    } catch (error) {
      console.error('Error creating user:', error);
      showToast(
        {
          title: 'Något gick fel',
          description: 'Försök igen senare',
        },
        { status: 'error' }
      );
      setLoading(false);
    }
  };

  const goBackToStepOne = () => {
    setCurrentStep(1);
    setUserLookupResult(null);
  };

  if (currentStep === 2) {
    return (
      <Stack spacing="4">
        <Button
          variant="ghost"
          size="sm"
          onClick={goBackToStepOne}
          alignSelf="flex-start"
          leftIcon={<Text>←</Text>}
          isDisabled={loading}
        >
          Tillbaka
        </Button>

        {userLookupResult?.exists && !userLookupResult.hasCompleteProfile && (
          <Text fontSize="sm" color="gray.600" mb={2}>
            Vi behöver lite mer information för att slutföra din registrering.
          </Text>
        )}

        <SignupForm
          formRef={formRef}
          onSubmit={handleSignupSubmit}
          defaultValues={{
            email: email,
            name: '',
            phone: '',
            acceptTerms: false,
            acceptPrivacyPolicy: false,
          }}
          disabledFields={{
            email: true,
          }}
          isLoading={loading}
          submitButtonLabel="Skicka inloggningslänk"
          showNameField={true}
          showPhoneField={true}
        />
      </Stack>
    );
  }

  return (
    <form id="auth-magic-link" onSubmit={handleMagicLinkSignIn}>
      <Stack spacing="4">
        <Input
          id="email"
          name="email"
          type="email"
          autoFocus
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          placeholder={t('email_input_placeholder')}
        />
        <Button
          variant="primary"
          type={email === '' ? 'button' : 'submit'}
          disabled={email === ''}
          isLoading={loading}
        >
          {loading ? t('loading_button_label') : t('button_label')}
        </Button>
      </Stack>
    </form>
  );
}

export { MagicLink };
